
import { useState, useRef, useEffect, useCallback } from "react";
import { AnimatePresence, motion, useMotionValue, useTransform } from "framer-motion";
import {
  FaBars,
  FaTimes,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaArrowRight,
  FaDownload,
  FaStar,
  FaCheckCircle,
  FaQuoteLeft,
  FaHeartbeat,
  FaGraduationCap,
  FaHandHoldingHeart,
  FaLeaf,
  FaUsers,
  FaShieldAlt,
  FaTint,
  FaSolarPanel,
  FaBookOpen,
  FaBriefcase,
  FaFemale,
  FaSun,
  FaSeedling,
  FaLinkedinIn,
  FaTwitter,
  FaFacebookF,
  FaYoutube,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaUserTie,
  FaPaperPlane,
} from "react-icons/fa";

import "./AFCSRPage.css";
import { WhyPartnerAFCSR } from "./WhyPartnerAFCSR";
import Interventions from "./INTERVENTIONS";
import AFCSROverview from "./AFCSROverview";

/* ==================================================================
   1. DATA
   ================================================================== */

const px = (id, w = 1400, h = 900) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&fit=crop&w=${w}&h=${h}`;

const portrait = (id) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&fit=crop&w=800&h=1000`;

const IMG = {
  hero: px("20556421", 1800, 1100),
  donation: px("6646918"),
  classroom: px("3231359"),
  girlMap: px("31864391"),
  healthOutdoor: px("33034061"),
  solar: px("14614021"),
  womenFarmers: px("20445215"),
  roadSafety: px("35829957"),
};

const NAV = [
  { label: "Overview", href: "#overview" },
  { label: "Vision", href: "#vision" },
  { label: "Pillars", href: "#pillars" },
  { label: "Interventions", href: "#interventions" },
  { label: "Impact", href: "#impact" },
  { label: "Leadership", href: "#leadership" },
];

const PILLARS = [
  {
    icon: FaGraduationCap,
    title: "Education",
    desc: "Access to quality, experiential learning with a focus on life-skills, hygiene, road safety and climate literacy.",
    stat: "4 Lakh+ students reached",
  },
  {
    icon: FaHandHoldingHeart,
    title: "Skilling & Livelihood",
    desc: "Employability, entrepreneurship and livelihoods for youth through training, mentoring and industry linkage.",
    stat: "7,756 students trained",
  },
  {
    icon: FaHeartbeat,
    title: "Health & Nutrition",
    desc: "Preventive healthcare, early screening, hygiene and nutrition awareness via camps, schools and policy dialogue.",
    stat: "450+ health camps",
  },
  {
    icon: FaLeaf,
    title: "Environment",
    desc: "Climate action through renewable energy adoption, afforestation, pollution awareness and community programmes.",
    stat: "35k+ saplings planted",
  },
  {
    icon: FaUsers,
    title: "Diversity & Inclusion",
    desc: "Inclusive ecosystems advancing gender equity, women's empowerment and participation of persons with disabilities.",
    stat: "Policy advocacy for PwDs",
  },
];

const STATS = [
  { value: 450, suffix: "+", label: "Health Camps", sub: "5 lakh+ lives impacted" },
  { value: 4, suffix: " Lakh+", label: "Students Reached", sub: "2,100+ teachers trained" },
  { value: 2400, prefix: "₹", suffix: " Cr", label: "COVID Response", sub: "Mobilised via ASSOCHAM" },
  { value: 300, suffix: "+", label: "Schools Partnered", sub: "Across 22 States & UTs" },
];

const BARS = [
  { label: "Education", pct: 92, color: "#DA2128" },
  { label: "Health & Nutrition", pct: 84, color: "#DB6400" },
  { label: "Skilling", pct: 71, color: "#F59C00" },
  { label: "Environment", pct: 66, color: "#2F7D4F" },
  { label: "Inclusion", pct: 58, color: "#8C5CF0" },
];

/* --- Areas of Intervention (the animated switch board) --- */
const INTERVENTIONS = [
  {
    id: "road-safety",
    tab: "Road Safety",
    title: "Road Safety",
    kicker: "Safer roads, saved lives",
    text: "Raising public awareness on road-safety challenges and promoting practical, enforceable solutions that reduce injuries and fatalities across Indian roads.",
    image: IMG.roadSafety,
    icon: FaShieldAlt,
    accent: "#DA2128",
    glow: "rgba(218,33,40,0.55)",
    metrics: [
      { k: "Awareness drives", v: "120+" },
      { k: "Stakeholder forums", v: "18" },
      { k: "Cities covered", v: "26" },
    ],
    points: [
      "Interactive school & campus sessions",
      "National conferences with policymakers",
      "Helmet, seat-belt & speed campaigns",
    ],
  },
  {
    id: "inclusion",
    tab: "Diversity & Inclusion",
    title: "Diversity and Inclusion",
    kicker: "Equity, by design",
    text: "Platforms where decision-makers exchange ideas, shape policy and share best practice — advancing gender diversity and the rights of persons with disabilities.",
    image: IMG.womenFarmers,
    icon: FaFemale,
    accent: "#DB6400",
    glow: "rgba(219,100,0,0.55)",
    metrics: [
      { k: "Women engaged", v: "12,000+" },
      { k: "Corporate partners", v: "90+" },
      { k: "Policy briefs", v: "14" },
    ],
    points: [
      "Gender-equity roundtables with industry",
      "Accessible workplace advocacy for PwDs",
      "Leadership pipelines for rural women",
    ],
  },
  {
    id: "education",
    tab: "Education & Life Skills",
    title: "Education & Life Skills",
    kicker: "Informed, confident citizens",
    text: "Between 2023–25 AFCSR equipped adolescents aged 10–19 with knowledge on bodily change, reproductive health and life skills through school-based curricula.",
    image: IMG.classroom,
    icon: FaBookOpen,
    accent: "#F59C00",
    glow: "rgba(245,156,0,0.55)",
    metrics: [
      { k: "Students reached", v: "4 Lakh+" },
      { k: "Teachers trained", v: "2,100+" },
      { k: "Schools partnered", v: "300+" },
    ],
    points: [
      "Life-skills & adolescent health modules",
      "Teacher capacity-building workshops",
      "Climate & road-safety literacy in class",
    ],
  },
  {
    id: "health",
    tab: "Health & Hygiene",
    title: "Healthcare, Hygiene & Nutrition",
    kicker: "Health for all",
    text: "Innovative programmes that encourage safe hygiene and healthy nutrition — awareness drives, school workshops and community camps that create lasting behaviour change.",
    image: IMG.healthOutdoor,
    icon: FaTint,
    accent: "#DA2128",
    glow: "rgba(218,33,40,0.5)",
    metrics: [
      { k: "Health camps", v: "450+" },
      { k: "Lives impacted", v: "5 Lakh+" },
      { k: "Screenings done", v: "1.2 Lakh" },
    ],
    points: [
      "Free multi-speciality screening camps",
      "WASH & menstrual-hygiene programmes",
      "Nutrition counselling for mothers",
    ],
  },
  {
    id: "environment",
    tab: "Climate & Energy",
    title: "Environment & Clean Energy",
    kicker: "Towards net-zero communities",
    text: "Renewable energy adoption in public facilities, large-scale afforestation and pollution awareness — building community resilience against a changing climate.",
    image: IMG.solar,
    icon: FaSolarPanel,
    accent: "#2F7D4F",
    glow: "rgba(47,125,79,0.55)",
    metrics: [
      { k: "Saplings planted", v: "35,000+" },
      { k: "Solar facilities", v: "10" },
      { k: "CO₂ offset", v: "1,800 t" },
    ],
    points: [
      "Rooftop solar for government facilities",
      "Miyawaki urban forests with citizens",
      "Air-quality & waste-segregation drives",
    ],
  },
];

const INITIATIVES = [
  { icon: FaBriefcase, title: "CSR Excellence Awards", note: "Recognising measurable corporate impact" },
  { icon: FaSun, title: "Solar for Public Facilities", note: "10 government buildings energised" },
  { icon: FaHandHoldingHeart, title: "Skill-to-Job Bridge", note: "7,756 youth trained & placed" },
  { icon: FaLeaf, title: "Green Bharat Drive", note: "35,000+ saplings, 22 states" },
];

const LEADERS = [
  {
    name: "Dr. Rajesh Mehta",
    role: "Chairman, AFCSR",
    img: portrait("15265578"),
    bio: "Steers the Foundation's national agenda, aligning industry capital with the development priorities of Viksit Bharat 2047.",
  },
  {
    name: "Anjali Verma",
    role: "Director — Programmes",
    img: portrait("7580822"),
    bio: "Designs and scales AFCSR's flagship education, health and life-skills programmes across 22 states and union territories.",
  },
  {
    name: "Prof. Arvind Rao",
    role: "Advisor — Policy & ESG",
    img: portrait("37605833"),
    bio: "Bridges research and regulation, authoring the Foundation's policy briefs on ESG disclosure and inclusive growth.",
  },
  {
    name: "Neha Patel",
    role: "Head — Community Engagement",
    img: portrait("7580837"),
    bio: "Builds grassroots coalitions with panchayats, schools and self-help groups to keep programmes community-owned.",
  },
];

const TICKER = [
  "450+ Health Camps",
  "4 Lakh+ Students Reached",
  "35,000+ Saplings Planted",
  "7,756 Youth Skilled",
  "22 States & UTs",
  "300+ Schools Partnered",
];

/* ==================================================================
   2. HELPERS
   ================================================================== */

function useInViewOnce(ref, amount = 0.25) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold: amount }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, amount, seen]);
  return seen;
}

function useCountUp(target, run, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf = 0;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / duration, 1);
      setVal(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, target, duration]);
  return val;
}

function SectionTitle({ eyebrow, title, italic, align = "center", sub }) {
  return (
    <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      <span className="inline-block text-[10px] font-bold uppercase tracking-[0.24em] text-[#DB6400]">
        {eyebrow}
      </span>
      <h2 className="font-display mt-3 text-3xl font-medium leading-[1.05] tracking-tight text-[#1A1714] md:text-4xl lg:text-[2.9rem]">
        {title} {italic && <span className="text-gradient-ember italic">{italic}</span>}
      </h2>
      {sub && <p className="mt-4 text-[13px] leading-relaxed text-[#1A1714]/60">{sub}</p>}
      <div
        className={`mt-5 h-[3px] w-16 rounded-full bg-gradient-to-r from-[#DA2128] to-[#F59C00] ${
          align === "center" ? "mx-auto" : ""
        }`}
      />
    </div>
  );
}

/* ==================================================================
   3. HEADER
   ================================================================== */

function Header() {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        const h = document.documentElement.scrollHeight - window.innerHeight;
        setSolid(y > 40);
        setProgress(h > 0 ? Math.min(y / h, 1) : 0);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div
        className="border-b backdrop-blur-xl transition-colors duration-300"
        style={{
          backgroundColor: solid ? "rgba(250,247,242,0.9)" : "rgba(250,247,242,0)",
          borderColor: solid ? "#EFE9DF" : "transparent",
        }}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3 lg:px-10">
          <a href="#top" className="group flex items-center gap-3">
            <span className="font-display relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#DA2128] to-[#DB6400] text-lg text-white shadow-lg shadow-[#DA2128]/25">
              A<span className="absolute inset-0 rounded-xl border border-white/30" />
            </span>
            <span className="leading-tight">
              <span
                className={`font-display block text-lg font-medium tracking-tight transition-colors ${
                  solid ? "text-[#1A1714]" : "text-white"
                }`}
              >
                AFCSR
              </span>
              <span
                className={`block text-[9px] font-semibold uppercase tracking-[0.18em] transition-colors ${
                  solid ? "text-[#6E6058]" : "text-white/65"
                }`}
              >
                ASSOCHAM Foundation for CSR
              </span>
            </span>
          </a>

          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                className={`rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${
                  solid
                    ? "text-[#1A1714]/70 hover:bg-[#FEF7F4] hover:text-[#DA2128]"
                    : "text-white/75 hover:bg-white/10 hover:text-white"
                }`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href="#leadership"
              className="hidden items-center gap-2 rounded-full bg-[#DA2128] px-5 py-2.5 text-xs font-bold text-white shadow-lg shadow-[#DA2128]/25 transition-transform hover:-translate-y-0.5 sm:inline-flex"
            >
              Partner with us <FaArrowRight className="h-2.5 w-2.5" />
            </a>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-label="Menu"
              className={`flex h-10 w-10 items-center justify-center rounded-xl border transition-colors lg:hidden ${
                solid
                  ? "border-[#EFE9DF] bg-white text-[#1A1714]"
                  : "border-white/25 bg-white/10 text-white"
              }`}
            >
              {open ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        <div
          className="h-0.5 origin-left bg-gradient-to-r from-[#DA2128] via-[#DB6400] to-[#F59C00]"
          style={{ transform: `scaleX(${progress})` }}
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="mx-4 mt-2 overflow-hidden rounded-[32px] border border-[#EFE9DF] bg-white/95 p-3 shadow-2xl shadow-[#1A1714]/10 backdrop-blur-xl lg:hidden"
          >
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between rounded-2xl px-4 py-3 text-sm font-semibold text-[#1A1714] hover:bg-[#FEF7F4]"
              >
                {n.label}
                <FaArrowRight className="h-3 w-3 text-[#DA2128]" />
              </a>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

/* ==================================================================
   4. HERO
   ================================================================== */

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden bg-[#12100E]">
      <div className="absolute inset-0">
        <motion.img
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: "easeOut" }}
          src={IMG.hero}
          alt="Community"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#12100E]/95 via-[#12100E]/75 to-[#12100E]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#12100E] via-transparent to-[#12100E]/60" />
      </div>

      {/* Paddings reduce ki hain yahan: pb-12 pt-20 (mobile) aur lg:pb-16 lg:pt-28 (desktop) */}
      <div className="relative mx-auto max-w-7xl px-5 pb-12 pt-20 lg:px-10 lg:pb-16 lg:pt-28">
        <div className="grid items-center gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          <motion.div
            initial={{ y: 34, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-[11px] font-semibold text-[#FEF7F4] backdrop-blur-md">
              <FaStar className="h-3 w-3 text-[#F59C00]" />
              Established 2013 · Trust · Social Arm of ASSOCHAM
            </span>

            <h1 className="font-display mt-4 text-[1.6rem] font-medium leading-[1.02] tracking-tight text-white sm:text-3xl lg:text-[2.3rem]">
              ASSOCHAM FOUNDATION FOR{" "}
              <span className="text-gradient-ember font-light italic">
                CORPORATE SOCIAL RESPONSIBILITY
              </span>
              <br />
              <span className="text-[#FEF7F4]">(AFCSR)</span>
            </h1>

            <p className="mt-4 max-w-xl text-[13.5px] leading-relaxed text-white/70 lg:text-sm">
              Rooted in the legacy of ASSOCHAM, India's Apex Chamber of Commerce, AFCSR bridges public policy and grassroots action — delivering impactful programmes in partnership with corporates, government bodies, development organisations, and communities.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="https://assocham.org/uploads/AFCSR_Brochure_2026.pdf"
                target="_blank"
                className="group inline-flex items-center gap-2 rounded-full bg-[#DA2128] px-6 py-2.5 text-[13px] font-bold text-white shadow-xl shadow-[#DA2128]/30 transition-transform hover:-translate-y-1"
              >
                AFCSR Brochure
                <FaDownload className="h-3 w-3 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </motion.div>

          {/* Right side media container height reduce ki hai: h-[20rem] */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="relative hidden h-[20rem] lg:block"
          >
            <div className="absolute right-0 top-0 h-52 w-48 overflow-hidden rounded-[24px] border border-white/15 shadow-2xl shadow-black/40">
              <img src={IMG.girlMap} alt="Education" className="h-full w-full object-cover" />
            </div>
            <div className="animate-float absolute bottom-0 left-2 h-48 w-52 overflow-hidden rounded-[24px] border border-white/15 shadow-2xl shadow-black/40">
              <img src={IMG.healthOutdoor} alt="Health camp" className="h-full w-full object-cover" />
            </div>
            <div className="glass absolute bottom-16 right-2 rounded-2xl px-3.5 py-2.5">
              <div className="flex items-center gap-2.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#F59C00]/20 text-[#F59C00]">
                  <FaSun className="h-3 w-3" />
                </span>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.16em] text-white/50">
                    Solar installed
                  </div>
                  <div className="text-xs font-bold text-white">10 Govt. facilities</div>
                </div>
              </div>
            </div>
            <div className="glass absolute left-0 top-2 rounded-2xl px-3.5 py-2.5">
              <div className="flex items-center gap-2.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#2F7D4F]/30 text-[#7BE0A6]">
                  <FaSeedling className="h-3 w-3" />
                </span>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.16em] text-white/50">
                    Green cover
                  </div>
                  <div className="text-xs font-bold text-white">35,000+ saplings</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ==================================================================
   5. OVERVIEW
   ================================================================== */

function Overview() {
  return (
    <section id="overview" className="relative overflow-hidden bg-[#FAF7F2] py-20 lg:py-28">
      <div className="animate-glow absolute -left-32 top-20 h-96 w-96 rounded-full bg-[#DA2128]/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <motion.div
            initial={{ x: -28, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
          >
            <SectionTitle
              eyebrow="Foundation Overview"
              title="Rooted in the legacy of"
              italic="ASSOCHAM"
              align="left"
            />
            <div className="mt-7 space-y-4 text-[13px] leading-relaxed text-[#1A1714]/70">
              <p>
                Rooted in the legacy of <strong className="text-[#1A1714]">ASSOCHAM</strong>, India's Apex Chamber of Commerce, the ASSOCHAM Foundation for Corporate Social Responsibility (AFCSR) was established as a Trust in <strong className="text-[#DA2128]">2013</strong> to bridge the gap between public policy and grassroots action, delivering impactful programmes in partnership with corporates, government bodies, development organisations, and communities.
              </p>
              <p>
                AFCSR is dedicated to identifying critical social, developmental, and environmental challenges that require urgent attention, measurable outcomes, and sustainable solutions, contributing to the vision of <strong className="text-[#1A1714]">Viksit Bharat 2047</strong>.
              </p>
              <p>
                As the social arm of ASSOCHAM, AFCSR plays a pivotal role in advancing inclusive and sustainable development through well-designed programmes and strategic partnerships. With a focus on education, healthcare, skilling, livelihoods, environmental sustainability, climate action, and community empowerment, the Foundation promotes responsible business practices, ESG-led initiatives, and industry–government collaboration to create long-term social impact and support India's development journey.
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-2">
              {[
                "Education",
                "Healthcare",
                "Skilling",
                "Livelihoods",
                "Environmental Sustainability",
                "Climate Action",
                "Community Empowerment",
              ].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-[#EFE9DF] bg-[#FEF7F4] px-3.5 py-1.5 text-[11px] font-semibold text-[#DB6400]"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ x: 28, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative"
          >
            <div className="group relative overflow-hidden rounded-[32px] shadow-2xl shadow-[#DA2128]/10">
              <img
                src={IMG.donation}
                alt="Collaboration"
                className="h-96 w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105 lg:h-[27rem]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#12100E]/75 via-[#12100E]/10 to-transparent" />
              <span className="sheen-layer" />
              <div className="glass absolute inset-x-5 bottom-5 rounded-[32px] p-5">
                <div className="font-display text-3xl text-white">05</div>
                <div className="text-xs text-white/75">
                  Thematic pillars aligned with national development priorities
                </div>
              </div>
            </div>
            <div className="absolute -left-5 -top-5 hidden rounded-[28px] border border-[#EFE9DF] bg-white p-4 shadow-xl md:block">
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#6E6058]">
                Since
              </div>
              <div className="font-display text-3xl leading-none text-[#DA2128]">2013</div>
            </div>
            <div className="absolute -bottom-6 -right-4 hidden w-44 rounded-[28px] border border-[#EFE9DF] bg-white p-4 shadow-xl lg:block">
              <div className="flex items-center gap-2 text-[11px] font-bold text-[#1A1714]">
                <FaCheckCircle className="h-3.5 w-3.5 text-[#2F7D4F]" /> Verified impact
              </div>
              <p className="mt-1 text-[10.5px] leading-snug text-[#6E6058]">
                Third-party audited programme reporting across 22 states.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}


/* ==================================================================
   6. VISION & MISSION
   ================================================================== */

function VisionMission() {
  const cards = [
    {
      tag: "Our Vision",
      text: "To be a catalyst for sustainable transformation by fostering collaboration between industry, government and communities.",
      note: "Driving inclusive development",
      grad: "from-[#DA2128] to-[#A8161C]",
      shadow: "shadow-[#DA2128]/25",
    },
    {
      tag: "Our Mission",
      text: "To drive impactful CSR through policy advocacy, meaningful public–private partnerships, sector recognition and knowledge creation.",
      note: "ESG-led initiatives",
      grad: "from-[#DB6400] to-[#F59C00]",
      shadow: "shadow-[#DB6400]/25",
    },
  ];

  return (
    <section id="vision" className="relative overflow-hidden bg-[#FAF7F2] pb-20 lg:pb-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <SectionTitle eyebrow="Our Direction" title="Vision &" italic="Mission" />
        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {cards.map((c, i) => (
            <motion.div
              key={c.tag}
              initial={{ y: 26, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              whileHover={{ y: -6 }}
              className={`grain relative overflow-hidden rounded-[32px] bg-gradient-to-br ${c.grad} p-9 text-white shadow-2xl ${c.shadow} md:p-12`}
            >
              <div className="animate-glow absolute -right-12 -top-12 h-52 w-52 rounded-full bg-white/15 blur-2xl" />
              <div className="animate-spin-slow absolute right-6 top-6 h-20 w-20 rounded-full border border-dashed border-white/30" />
              <h3 className="font-display relative text-2xl md:text-[1.7rem]">{c.tag}</h3>
              <p className="relative mt-3 max-w-md text-[13px] font-light leading-relaxed text-white/90">
                {c.text}
              </p>
              <div className="relative mt-7 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-[11px] font-bold backdrop-blur">
                <FaCheckCircle className="h-3 w-3" /> {c.note}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==================================================================
   7. THEMATIC PILLARS + KEY INITIATIVES
   ================================================================== */

function Pillars() {
  return (
    <section id="pillars" className="relative overflow-hidden bg-[#FEF7F4]/60 py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <SectionTitle
          eyebrow="Core Focus"
          title="Thematic"
          italic="Pillars"
          sub="Our work is anchored around five core pillars aligned with national priorities — creating measurable outcomes across health, education, livelihoods, environment and inclusion."
        />

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ y: 26, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="group relative overflow-hidden rounded-[28px] border border-[#EFE9DF] bg-white p-7 shadow-xl shadow-[#DA2128]/5 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-[#DA2128]/10"
            >
              <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[#DA2128]/5 transition-transform duration-500 group-hover:scale-150" />
              <div className="relative flex items-start justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FEF7F4] text-[#DA2128] transition-colors duration-300 group-hover:bg-[#DA2128] group-hover:text-white">
                  <p.icon className="h-5 w-5" />
                </span>
                {/* <span className="font-display text-2xl text-[#EFE9DF] transition-colors group-hover:text-[#DA2128]/25">
                  0{i + 1}
                </span> */}
              </div>
              <h3 className="font-display relative mt-5 text-lg text-[#1A1714] transition-colors group-hover:text-[#DA2128]">
                {p.title}
              </h3>
              <p className="relative mt-2 text-[12.5px] leading-relaxed text-[#1A1714]/60">
                {p.desc}
              </p>
              <div className="relative mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#FEF7F4] px-3 py-1.5 text-[10.5px] font-bold text-[#DB6400]">
                <FaStar className="h-2.5 w-2.5" /> {p.stat}
              </div>
            </motion.div>
          ))}

        </div>

        {/* key initiatives strip */}
        {/* <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {INITIATIVES.map((k, i) => (
            <motion.div
              key={k.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="flex items-center gap-3 rounded-2xl border border-[#EFE9DF] bg-white/70 px-4 py-4 backdrop-blur transition-colors hover:border-[#DA2128]/30 hover:bg-white"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#DA2128]/10 to-[#F59C00]/10 text-[#DA2128]">
                <k.icon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <div className="truncate text-[12.5px] font-bold text-[#1A1714]">{k.title}</div>
                <div className="truncate text-[11px] text-[#6E6058]">{k.note}</div>
              </div>
            </motion.div>
          ))}
        </div> */}
      </div>
    </section>
  );
}


/* ==================================================================
   9. IMPACT
   ================================================================== */

function StatCard({ stat, index, run }) {
  const v = useCountUp(stat.value, run);
  const display = stat.value >= 100 ? Math.round(v).toLocaleString("en-IN") : v.toFixed(0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      className="group relative overflow-hidden rounded-[26px] border border-[#EFE9DF] bg-white p-6 shadow-lg shadow-[#DA2128]/5 transition-transform hover:-translate-y-1.5"
    >
      <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#DA2128] via-[#DB6400] to-[#F59C00] opacity-70" />
      <div className="font-display text-[2.1rem] leading-none text-[#1A1714]">
        {stat.prefix || ""}
        {display}
        {stat.suffix || ""}
      </div>
      <div className="mt-2 text-[12.5px] font-bold text-[#1A1714]">{stat.label}</div>
      <div className="mt-0.5 text-[11px] text-[#6E6058]">{stat.sub}</div>
      <div className="absolute -bottom-8 -right-8 h-24 w-24 rounded-full bg-[#DA2128]/5 transition-transform duration-500 group-hover:scale-150" />
    </motion.div>
  );
}

function Leadership() {
  // Council Leaders Data inside function
  const LEADERS = [
    {
      name: "Anil Rajput",
      role: "Council Chair",
      designation: "Senior Vice President, Corporate Affairs, ITC Limited",
      img: "https://assocham.org/assets/images/sector_council/1785146713_Mr%20AR%20Pic.jpg",
      bio: "Senior Vice President, Corporate Affairs at ITC Limited, steering national corporate affairs and policy engagement initiatives.",
    },
    {
      name: "Ms. Rumi Mallick Mitra",
      role: "Council Co-Chair",
      designation:
        "Global Head, Director, Corporate Social Responsibility and Sustainability, EY Global Delivery Services",
      img: "https://assocham.org/assets/images/sector_council/1785146640_Ms.%20Rumi%20Pic.jpg",
      bio: "Global Head and Director of CSR and Sustainability at EY Global Delivery Services, championing sustainable impact programs globally.",
    },
  ];

  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const prev = useCallback(
    () => setIndex((i) => (i === 0 ? LEADERS.length - 1 : i - 1)),
    [LEADERS.length]
  );
  const next = useCallback(
    () => setIndex((i) => (i === LEADERS.length - 1 ? 0 : i + 1)),
    [LEADERS.length]
  );
  const close = useCallback(() => {
    setOpen(false);
    document.body.style.overflow = "";
  }, []);

  const openAt = (i) => {
    setIndex(i);
    setOpen(true);
    document.body.style.overflow = "hidden";
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, prev, next]);

  useEffect(
    () => () => {
      document.body.style.overflow = "";
    },
    []
  );

  const leader = LEADERS[index];

  return (
    <section id="leadership" className="relative overflow-hidden bg-white py-20 lg:py-28">
      <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
        
        {/* Header Section */}
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#F59C00]/30 bg-[#FEF7F4] px-4 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#DB6400]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#F59C00]" /> COUNCILS
          </span>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#1A1714] md:text-5xl">
            Council Leadership
          </h2>
          {/* Double Accent Bar */}
          <div className="mt-4 flex items-center justify-center gap-1.5">
            <span className="h-[3px] w-8 rounded-full bg-[#DA2128]" />
            <span className="h-[3px] w-3 rounded-full bg-[#F59C00]" />
            <span className="h-[3px] w-2 rounded-full bg-[#F59C00]" />
          </div>
        </div>

        {/* Flex Cards Container */}
        <div className="mt-14 flex flex-col flex-wrap justify-center gap-6 lg:flex-row">
          {LEADERS.map((l, i) => (
            <motion.div
              key={l.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              onClick={() => openAt(i)}
              className="group relative flex flex-1 min-w-[320px] max-w-[620px] cursor-pointer overflow-hidden rounded-[32px] border border-[#EFE9DF]/80 bg-[#FAF7F2]/50 p-6 shadow-sm transition-all duration-300 hover:bg-white hover:shadow-xl hover:shadow-[#1A1714]/5 md:p-7"
            >
              {/* Background Decorative Shape Top-Right */}
              <div className="absolute -right-8 -top-8 h-36 w-36 rounded-full bg-[#FEF7F4] transition-transform group-hover:scale-110" />

              {/* Background Decorative Leaf Outline Bottom-Right */}
              <div className="pointer-events-none absolute bottom-4 right-6 text-[#DA2128]/10 transition-colors group-hover:text-[#DA2128]/20">
                <FaSeedling className="h-12 w-12" />
              </div>

              <div className="relative z-10 flex w-full flex-col gap-6 sm:flex-row sm:items-center">
                {/* Image Wrapper with Gradient Border */}
                <div className="relative shrink-0">
                  <div className="h-36 w-36 overflow-hidden rounded-[24px] bg-gradient-to-tr from-[#DA2128] via-[#DB6400] to-[#F59C00] p-[3px] shadow-md transition-transform duration-500 group-hover:scale-[1.02] sm:h-40 sm:w-40">
                    <img
                      src={l.img}
                      alt={l.name}
                      loading="lazy"
                      className="h-full w-full rounded-[21px] object-cover object-top"
                    />
                  </div>
                </div>

                {/* Content Right */}
                <div className="flex flex-1 flex-col items-start justify-center">
                  {/* Red Pill Badge */}
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DA2128] px-3.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm shadow-[#DA2128]/20">
                    <FaUsers className="h-2.5 w-2.5" />
                    {l.role}
                  </span>

                  {/* Name */}
                  <h3 className="font-display mt-3 text-2xl font-bold tracking-tight text-[#1A1714] transition-colors group-hover:text-[#DA2128] md:text-[1.6rem]">
                    {l.name}
                  </h3>

                  {/* Designation */}
                  <p className="mt-1 text-xs font-semibold leading-relaxed text-[#6E6058] line-clamp-2">
                    {l.designation}
                  </p>

                  {/* Small Red/Yellow Bar Accent */}
                  <div className="mt-3.5 flex items-center gap-1">
                    <span className="h-[2px] w-6 rounded-full bg-[#DA2128]" />
                    <span className="h-[2px] w-2 rounded-full bg-[#F59C00]" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Profile Detail Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#12100E]/85 p-4 backdrop-blur-xl"
          >
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <FaTimes />
            </button>

            <motion.div
              key={index}
              initial={{ scale: 0.94, opacity: 0, y: 18 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 18 }}
              transition={{ duration: 0.32 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl overflow-hidden rounded-[32px] bg-[#FEF7F4] shadow-2xl"
            >
              <div className="grid md:grid-cols-2">
                <div className="relative h-64 md:h-[28rem]">
                  <img
                    src={leader.img}
                    alt={leader.name}
                    className="h-full w-full object-cover object-top"
                  />
                  <div className="absolute inset-0 hidden bg-gradient-to-r from-transparent to-[#FEF7F4] md:block" />
                </div>
                <div className="flex flex-col justify-center p-7 md:p-10">
                  <FaQuoteLeft className="h-5 w-5 text-[#DA2128]/25" />
                  <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.22em] text-[#DB6400]">
                    Leadership profile
                  </div>
                  <h3 className="font-display mt-2 text-2xl text-[#1A1714] md:text-[1.7rem]">
                    {leader.name}
                  </h3>
                  <p className="mt-1 text-[12.5px] font-bold text-[#DA2128]">
                    {leader.role}
                  </p>
                  <p className="mt-0.5 text-[12px] font-medium text-[#1A1714]/75">
                    {leader.designation}
                  </p>
                  <p className="mt-4 text-[13px] leading-relaxed text-[#1A1714]/65">
                    {leader.bio}
                  </p>

                  <div className="mt-7 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={prev}
                      aria-label="Previous"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DA2128] text-white shadow-lg shadow-[#DA2128]/25 transition-transform hover:scale-105"
                    >
                      <FaChevronLeft className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={next}
                      aria-label="Next"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DA2128] text-white shadow-lg shadow-[#DA2128]/25 transition-transform hover:scale-105"
                    >
                      <FaChevronRight className="h-3 w-3" />
                    </button>
                    <span className="ml-auto text-[11px] font-semibold text-[#6E6058]">
                      {index + 1} / {LEADERS.length}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    subject: "CSR Partnership",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Perform submission logic here (e.g. API call)
    setSubmitted(true);
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-[#FAF7F2] py-20 lg:py-28">
      {/* Background Glow */}
      <div className="animate-glow absolute -right-24 top-1/4 h-80 w-80 rounded-full bg-[#DA2128]/10 blur-3xl" />
      
      <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
        
        {/* Section Header */}
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#F59C00]/30 bg-[#FEF7F4] px-4 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#DB6400]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#F59C00]" /> GET IN TOUCH
          </span>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#1A1714] md:text-5xl">
            Partner With <span className="text-[#DA2128]">AFCSR</span>
          </h2>
          <div className="mt-4 flex items-center justify-center gap-1.5">
            <span className="h-[3px] w-8 rounded-full bg-[#DA2128]" />
            <span className="h-[3px] w-3 rounded-full bg-[#F59C00]" />
            <span className="h-[3px] w-2 rounded-full bg-[#F59C00]" />
          </div>
          <p className="mt-4 max-w-2xl text-xs text-[#6E6058] sm:text-sm">
            Reach out to collaborate on high-impact CSR initiatives, policy engagement, or community development programs across India.
          </p>
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-12 lg:items-start">
          
          {/* LEFT COLUMN: Contact Cards & Leadership Highlight */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-6 lg:col-span-5"
          >
            {/* Leadership Highlight Card */}
            {/* <div className="relative overflow-hidden rounded-[28px] border border-[#EFE9DF] bg-white p-6 shadow-xl shadow-[#DA2128]/5 sm:p-7">
              <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#FEF7F4]" />
              
              <div className="relative z-10">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DA2128] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                  <FaUserTie className="h-3 w-3" /> Council Leadership
                </span>

                <div className="mt-4 flex items-start gap-4">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-gradient-to-tr from-[#DA2128] to-[#F59C00] p-[2px]">
                    <img
                      src="https://assocham.org/assets/images/sector_council/1785146713_Mr%20AR%20Pic.jpg"
                      alt="Anil Rajput"
                      className="h-full w-full rounded-[14px] object-cover object-top"
                    />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#DB6400]">
                      Council Chair
                    </span>
                    <h4 className="font-display text-xl font-bold text-[#1A1714]">
                      Anil Rajput
                    </h4>
                    <p className="mt-0.5 text-xs text-[#6E6058]">
                      Senior Vice President, Corporate Affairs, ITC Limited
                    </p>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Direct Contact Info Card */}
            <div className="rounded-[28px] border border-[#EFE9DF] bg-[#FAF7F2]/60 p-6 shadow-sm sm:p-7">
              <h4 className="font-display text-lg font-bold text-[#1A1714]">
                Contact Channels
              </h4>

              <div className="mt-5 space-y-4 text-xs">
               
                {/* Office Location */}
                <div className="flex items-start gap-3.5 rounded-2xl border border-[#EFE9DF] bg-white p-3.5 shadow-sm">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F59C00]/10 text-[#DB6400]">
                    <FaMapMarkerAlt className="h-4 w-4" />
                  </span>
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-[#6E6058]">
                      Headquarters
                    </span>
                    <p className="mt-0.5 leading-relaxed font-semibold text-[#1A1714]">
                      ASSOCHAM Corporate Office, YMCA Cultural Centre, New Delhi - 110001
                    </p>
                  </div>
                </div>

                 {/* Email Direct */}
                <a
                  href="mailto:afcsr@afcsr.in"
                  className="group flex items-center gap-3.5 rounded-2xl border border-[#EFE9DF] bg-white p-3.5 shadow-sm transition-all hover:border-[#DA2128]/30 hover:shadow-md"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#DA2128]/10 text-[#DA2128] transition-colors group-hover:bg-[#DA2128] group-hover:text-white">
                    <FaEnvelope className="h-4 w-4" />
                  </span>
                  <div className="overflow-hidden">
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-[#6E6058]">
                      Official Email
                    </span>
                    <span className="truncate font-semibold text-[#1A1714] group-hover:text-[#DA2128]">
                      afcsr@afcsr.in
                    </span>
                  </div>
                </a>


                {/* Phone */}
                <div className="flex items-center gap-3.5 rounded-2xl border border-[#EFE9DF] bg-white p-3.5 shadow-sm">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#2F7D4F]/10 text-[#2F7D4F]">
                    <FaPhoneAlt className="h-4 w-4" />
                  </span>
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-[#6E6058]">
                      Phone
                    </span>
                    <span className="font-semibold text-[#1A1714]">
                      +91 11 4655 0555
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: Interactive Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <div className="rounded-[32px] border border-[#EFE9DF] bg-white p-7 shadow-2xl shadow-[#DA2128]/5 sm:p-10">
              {submitted ? (
                <div className="flex min-h-[380px] flex-col items-center justify-center text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#2F7D4F]/10 text-[#2F7D4F]">
                    <FaCheckCircle className="h-8 w-8" />
                  </span>
                  <h3 className="font-display mt-4 text-2xl font-bold text-[#1A1714]">
                    Message Sent Successfully
                  </h3>
                  <p className="mt-2 max-w-md text-xs leading-relaxed text-[#6E6058] sm:text-sm">
                    Thank you for writing to us. Our CSR team will review your query and get back to you shortly at{" "}
                    <strong className="text-[#1A1714]">{formData.email}</strong>.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({
                        name: "",
                        email: "",
                        organization: "",
                        subject: "CSR Partnership",
                        message: "",
                      });
                    }}
                    className="mt-6 rounded-full bg-[#1A1714] px-6 py-2.5 text-xs font-bold text-white transition-all hover:bg-[#DA2128]"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <h3 className="font-display text-2xl font-bold text-[#1A1714]">
                    Send a Message
                  </h3>
                  <p className="text-xs text-[#6E6058]">
                    Fill out the form below to initiate discussions for corporate partnerships, project implementation, or inquiries.
                  </p>

                  <div className="grid gap-5 sm:grid-cols-2">
                    {/* Full Name */}
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-[#1A1714]">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="e.g. Dev Kumar"
                        className="mt-1.5 w-full rounded-xl border border-[#EFE9DF] bg-[#FAF7F2]/40 px-4 py-3 text-xs text-[#1A1714] outline-none transition-all focus:border-[#DA2128] focus:bg-white focus:ring-2 focus:ring-[#DA2128]/10"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-[#1A1714]">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="name@company.com"
                        className="mt-1.5 w-full rounded-xl border border-[#EFE9DF] bg-[#FAF7F2]/40 px-4 py-3 text-xs text-[#1A1714] outline-none transition-all focus:border-[#DA2128] focus:bg-white focus:ring-2 focus:ring-[#DA2128]/10"
                      />
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    {/* Organization */}
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-[#1A1714]">
                        Organization / Company
                      </label>
                      <input
                        type="text"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        placeholder="e.g. ITC Limited"
                        className="mt-1.5 w-full rounded-xl border border-[#EFE9DF] bg-[#FAF7F2]/40 px-4 py-3 text-xs text-[#1A1714] outline-none transition-all focus:border-[#DA2128] focus:bg-white focus:ring-2 focus:ring-[#DA2128]/10"
                      />
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-[#1A1714]">
                        Inquiry Purpose
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="mt-1.5 w-full rounded-xl border border-[#EFE9DF] bg-[#FAF7F2]/40 px-4 py-3 text-xs text-[#1A1714] outline-none transition-all focus:border-[#DA2128] focus:bg-white focus:ring-2 focus:ring-[#DA2128]/10"
                      >
                        <option value="CSR Partnership">CSR Partnership</option>
                        <option value="Program Collaboration">Program Collaboration</option>
                        <option value="ESG Advisory">ESG & Policy Inquiry</option>
                        <option value="General Query">General Query</option>
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-[#1A1714]">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      rows="4"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Share details regarding your program requirements or collaboration proposal..."
                      className="mt-1.5 w-full resize-none rounded-xl border border-[#EFE9DF] bg-[#FAF7F2]/40 px-4 py-3 text-xs text-[#1A1714] outline-none transition-all focus:border-[#DA2128] focus:bg-white focus:ring-2 focus:ring-[#DA2128]/10"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#DA2128] py-3.5 text-xs font-bold text-white shadow-lg shadow-[#DA2128]/25 transition-all duration-300 hover:bg-[#c01b22] hover:shadow-xl hover:shadow-[#DA2128]/35"
                  >
                    <span>Submit Proposal / Query</span>
                    <FaPaperPlane className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                  </button>
                </form>
              )}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ==================================================================
   12. PAGE
   ================================================================== */

export default function AFCSRPage() {
  return (
    <div className="afcsr-page min-h-screen bg-[#FAF7F2] text-[#1A1714] antialiased">
      {/* <Header /> */}
      <main>
        <Hero />
        <AFCSROverview />
        <VisionMission />
         <Leadership />
        <Pillars />
        <Interventions />
        <WhyPartnerAFCSR />
        {/* <Impact /> */}
        <ContactSection />
      </main>
      {/* <Footer /> */}
    </div>
  );
}
